import { useQuery } from '@tanstack/react-query';

import { api } from 'services/api';

export type BorrowerFile = {
  id: string;
  name: string;
  contentType: string;
  sizeBytes: number | null;
  uploadedAt: string;
  uploadedBy: string;
};

type BorrowerFilesResponse = {
  data: BorrowerFile[];
  total: number;
  offset: number;
  limit: number;
};

type FileDownloadResponse = {
  data: {
    downloadUrl: string;
    fileName: string;
    contentType: string;
  };
};

const fetchBorrowerFiles = async ({
  borrowerId,
  offset,
  limit,
}: {
  borrowerId: string;
  offset: number;
  limit: number;
}) => {
  const res = await api.get<BorrowerFilesResponse>(
    `/borrowers/${borrowerId}/files`,
    { params: { offset, limit } },
  );
  return res.data;
};

const fetchFileDownloadUrl = async ({
  borrowerId,
  fileId,
}: {
  borrowerId: string;
  fileId: string;
}) => {
  const res = await api.get<FileDownloadResponse>(
    `/borrowers/${borrowerId}/files/${fileId}/download`,
  );
  return res.data.data;
};

export const useBorrowerFiles = (
  borrowerId: string | undefined,
  page: number,
  perPage: number,
) => {
  const offset = (page - 1) * perPage;

  return useQuery({
    queryKey: ['borrowerFiles', borrowerId, page, perPage],
    queryFn: () =>
      fetchBorrowerFiles({
        borrowerId: borrowerId!,
        offset,
        limit: perPage,
      }),
    enabled: !!borrowerId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useFileDownloadUrl = (
  borrowerId: string | undefined,
  fileId: string | undefined,
) => {
  return useQuery({
    queryKey: ['fileDownload', borrowerId, fileId],
    queryFn: () =>
      fetchFileDownloadUrl({
        borrowerId: borrowerId!,
        fileId: fileId!,
      }),
    enabled: false,
  });
};

export { fetchFileDownloadUrl };
